import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Award, Pencil, Share2, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { AssignmentDialog } from '@/components/AssignmentDialog'
import { CertificateRequirements } from '@/components/CertificateRequirements'
import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { MultiSelect } from '@/components/MultiSelect'
import { useCoursesScope } from '@/pages/CoursesHub/useCoursesScope'
import {
  deleteCertificate as deleteCertificateRecord,
  getCertificates,
} from '@/services/certificates.service'
import { getCourses } from '@/services/courses.service'
import { getEntityCourses } from '@/services/entity_courses.service'
import {
  getOrganizationCertificateAssignment,
  setOrganizationCertificateAssignment,
} from '@/services/organization.service'
import { UserStoreContext } from '@/stores/UserStore'
import { hasAnyFilterSelected, toast } from '@/utils/helpers'

import CreateCertificateModal from './CreateCertificateModal'

const DEFAULT_FILTERS = {
  course: [],
}

const assignmentLabel = (row) => {
  if (row.assignedToAll) return 'All facilities'
  const count = row.assignedFacilityCount ?? 0
  if (count > 0) return `${count} ${count === 1 ? 'facility' : 'facilities'}`
  return 'Not assigned'
}

const Certificates = observer(() => {
  // Context
  const navigate = useNavigate()
  const { isCustom, isFacility, isOrg, scopeId, scopeArgs, hubBase } = useCoursesScope()
  const { user, isEntityOwner, isEntityAdmin, isXverseAdmin } = useContext(UserStoreContext)
  // Org + facility management is route-gated to owners/admins; the RescueX
  // admin catalog requires xverse admin.
  const canManage = (() => {
    if (isFacility) return user.isXverseAdmin || isEntityOwner || isEntityAdmin
    if (isOrg) return true
    return isXverseAdmin
  })()

  // State
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [assignCert, setAssignCert] = useState(null)

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { data: courses } = useQuery({
    queryKey: ['courses', scopeId],
    queryFn: () =>
      isCustom
        ? getEntityCourses({
            ...scopeArgs,
            pagination: { pageSize: 1000, pageIndex: 0 },
            // Mirror the page-level filter — the Course Module multiselect
            // only offers this scope's own courses, matching what shows in
            // the Custom Courses list.
            filters: { state: 'Published', is_xverse_course: false },
          })
        : getCourses(null, null, null, null, true),
    placeholderData: keepPreviousData,
  })
  // Custom scope: hide xverse-owned certificates so the management view shows
  // only this scope's custom courses (matches the "Custom Courses" tab label
  // in CoursesHub).
  const scopedFilters = isCustom ? { ...filters, is_xverse_certificate: false } : filters
  const { data: certificates, isPending } = useQuery({
    queryKey: ['certificates', scopeId, sorting, pagination, searchTerm, scopedFilters],
    queryFn: () =>
      getCertificates({ ...scopeArgs, sorting, pagination, searchTerm, filters: scopedFilters }),
    placeholderData: keepPreviousData,
  })
  const { isPending: pendingDelete, mutate: deleteCertificate } = useMutation({
    mutationFn: (data) => deleteCertificateRecord({ ...scopeArgs, id: data.id }),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Course deleted successfully')

      // Refresh certificates list
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
    },
  })

  // Filter or search changes shrink the result set, so a deep page can 404
  // from the paginator. Reset to page 0 in the same handler so React batches
  // both updates into a single render.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  // hubBase already resolves per scope (facility / org / admin), so the cert
  // detail path is uniform.
  const certPath = (id) => `${hubBase}/courses/${id}`

  // Org-shared certs surface read-only in the facility list — owned at the org.
  const isSharedIn = (row) => isFacility && Boolean(row.organization)

  const COURSES = useMemo(() => {
    if (!courses) return []

    return courses.results.map((course) => ({
      label: course.name,
      id: course.id,
    }))
  }, [courses])

  return (
    <>
      {/* @fixme: This is a hacky way to make the table filters responsive */}
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          {(searchTerm || hasAnyFilterSelected(filters)) && (
            <Button
              className="visible md:hidden"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setFilters(DEFAULT_FILTERS)
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
          <Button
            className="visible md:hidden"
            disabled={!canManage}
            onClick={() => setShowCertificateModal(true)}
          >
            Add Course
          </Button>
        </div>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for Course"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />
          <MultiSelect
            placeholder="Filter by Course Module"
            options={COURSES}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                course: value || [],
              }))
              resetToFirstPage()
            }}
            value={filters.course}
            width="w-full md:w-56"
          />
          {(searchTerm || hasAnyFilterSelected(filters)) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setFilters(DEFAULT_FILTERS)
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button
          className="hidden md:block"
          disabled={!canManage}
          onClick={() => setShowCertificateModal(true)}
        >
          Add Course
        </Button>
      </div>

      {(() => {
        const showEmptyState =
          isCustom &&
          !isPending &&
          !searchTerm &&
          !hasAnyFilterSelected(filters) &&
          (certificates?.results?.length ?? 0) === 0
        if (showEmptyState) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Custom Courses yet</h3>
                <p className="text-muted-foreground mx-auto mb-4 max-w-md text-sm">
                  A Custom Course is a certification your members earn. It groups one or more
                  Course Modules into a single requirement.
                </p>
                <div className="flex flex-col items-center gap-2">
                  <Button disabled={!canManage} onClick={() => setShowCertificateModal(true)}>
                    Create Custom Course
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`${hubBase}/course-modules`)}
                  >
                    Need a Course Module first? Build one →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        }
        return (
          <DataTable
            columns={[
              {
                accessorKey: 'name',
                header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
                cell: ({ row }) =>
                  isSharedIn(row.original) ? (
                    <span className="flex items-center gap-2">
                      {row.original.name}
                      <Badge variant="secondary">Shared</Badge>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-action cursor-pointer text-left hover:underline"
                      disabled={!canManage}
                      onClick={() => navigate(certPath(row.original.id))}
                    >
                      {row.original.name}
                    </button>
                  ),
                size: 300,
              },
              {
                id: 'requirements',
                header: () => <span className="uppercase">Modules</span>,
                cell: ({ row }) => <CertificateRequirements certificate={row.original} />,
                sortable: false,
              },
              {
                id: 'training',
                header: () => <span className="uppercase">Hands-On Training</span>,
                cell: ({ row }) => {
                  const { training } = row.original
                  if (!training) {
                    return <span className="text-muted-foreground">None</span>
                  }
                  return <span>{training.name}</span>
                },
                sortable: false,
                size: 200,
              },
              isOrg && {
                id: 'assigned',
                header: () => <span className="uppercase">Assigned to</span>,
                size: 140,
                cell: ({ row }) => {
                  const assigned =
                    row.original.assignedToAll || row.original.assignedFacilityCount > 0
                  return (
                    <span className={assigned ? '' : 'text-muted-foreground'}>
                      {assignmentLabel(row.original)}
                    </span>
                  )
                },
              },
              isOrg && {
                id: 'assign',
                size: 80,
                cell: ({ row }) => (
                  <Button
                    className="place-self-end"
                    variant="outline"
                    size="sm"
                    onClick={() => setAssignCert(row.original)}
                  >
                    <Share2 className="mr-1 h-4 w-4" />
                    Assign
                  </Button>
                ),
              },
              {
                id: 'edit',
                cell: ({ row }) =>
                  isSharedIn(row.original) ? null : (
                    <Button
                      className="place-self-end"
                      variant="outline"
                      size="sm"
                      disabled={!canManage}
                      onClick={() => navigate(certPath(row.original.id))}
                    >
                      <Pencil className="mr-1 h-4 w-4" />
                      Edit
                    </Button>
                  ),
                size: 80,
              },
              {
                id: 'delete',
                cell: ({ row }) =>
                  isSharedIn(row.original) ? null : (
                    <ConfirmButton
                      confirmContent={`Are you sure you want to delete the certificate: ${row.original.name}?`}
                      confirmTitle="Delete Certificate"
                      className="place-self-end"
                      disabled={!canManage}
                      variant="destructive"
                      size="sm"
                      loading={pendingDelete}
                      onClick={() => deleteCertificate(row.original)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete certificate</span>
                    </ConfirmButton>
                  ),
                size: 80,
              },
            ].filter(Boolean)}
            data={certificates?.results || []}
            loading={isPending}
            pagination={pagination}
            rowCount={certificates?.count || 0}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
          />
        )
      })()}

      {showCertificateModal && (
        <CreateCertificateModal
          {...scopeArgs}
          close={(id) => {
            setShowCertificateModal(false)
            if (id) navigate(certPath(id))
          }}
        />
      )}

      {assignCert && (
        <AssignmentDialog
          open
          setOpen={(v) => {
            if (!v) setAssignCert(null)
          }}
          organizationId={scopeId}
          title={`Assign "${assignCert.name}"`}
          fetchAssignment={() =>
            getOrganizationCertificateAssignment({
              organizationId: scopeId,
              certificateId: assignCert.id,
            })
          }
          saveAssignment={(payload) =>
            setOrganizationCertificateAssignment({
              organizationId: scopeId,
              certificateId: assignCert.id,
              ...payload,
            })
          }
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['certificates'] })}
        />
      )}
    </>
  )
})

export default Certificates
